import React, { useEffect, useState } from 'react'
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc'
import arrayMove from 'array-move'
import classNames from 'classnames'

import DragIcon from '../icons/Drag'
import XIcon from '../icons/X'
import DownloadIcon from '../icons/Download'
import styles from './ExportButton.module.css'

interface Props {
  exportList: Export[]
  setExportList: React.Dispatch<React.SetStateAction<Export[]>>
  onExportClick: () => void
  className?: string
}

type DragHandleProps = { className?: string }
const DragHandle = SortableHandle<DragHandleProps>(
  ({ className }: DragHandleProps) => <DragIcon className={className} />
)

type SortableItemProps = { onRemove: () => void; text: string }
const SortableItem = SortableElement<SortableItemProps>(
  ({ onRemove, text }: SortableItemProps) => (
    <div className={styles.listElement}>
      <div className={styles.listDragAndName}>
        <DragHandle className={styles.dragHandle} />
        <span className={styles.chartName}>{text}</span>
      </div>
      <button onClick={onRemove}>
        <XIcon className={styles.elementRemoveIcon} />
      </button>
    </div>
  )
)

type SortableListProps = {
  onRemove: (id: string) => void
  list: Export[]
}
const SortableList = SortableContainer<SortableListProps>(
  ({ onRemove, list }: SortableListProps) => (
    <div className={styles.listElementsContainer}>
      {list.map(({ id, name }, index) => (
        <SortableItem
          key={id}
          index={index}
          text={name}
          onRemove={() => onRemove(id)}
        />
      ))}
    </div>
  )
)

type State = 'hidden' | 'collapsed' | 'expanded'

const ExportButton: React.FC<Props> = (props) => {
  const [state, setState] = useState<State>('hidden')

  useEffect(() => {
    if (state === 'hidden') {
      if (props.exportList.length > 0) {
        setState('collapsed')
      }
    } else if (props.exportList.length === 0) {
      setState('hidden')
    }
  }, [state, props.exportList])

  const handleExpand = () => setState('expanded')

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => props.setExportList((curList) => arrayMove(curList, oldIndex, newIndex))

  const handleRemove = (id: string) =>
    props.setExportList((curList) =>
      curList.filter((element) => element.id !== id)
    )

  if (state === 'hidden') {
    return null
  }

  return (
    <div className={classNames(styles.container, props.className)}>
      {state === 'collapsed' ? (
        <button className={styles.collapsedButton} onClick={handleExpand}>
          <div className={styles.counterBadge}>{props.exportList.length}</div>
          <DownloadIcon className={styles.downloadIcon} />
        </button>
      ) : (
        <div className={styles.listContainer}>
          <div className={styles.listTitle}>Exportar gráficos</div>
          <SortableList
            list={props.exportList}
            onSortEnd={handleSortEnd}
            onRemove={handleRemove}
            helperClass={styles.sortableHelper}
            useDragHandle
          />
          <button className={styles.exportButton} onClick={props.onExportClick}>
            Exportar para PPT
          </button>
        </div>
      )}
    </div>
  )
}

export default ExportButton
